import { getSessionToken, handleSessionExpired } from "./dashboard-session";

export class ApiError extends Error {
  status: number;
  data?: any;
  constructor(status: number, message: string, data?: any) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export const CORE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export const INTEL = `${CORE}/api/v1/scout`;

export const getToken = getSessionToken;

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function failure(res: Response): Promise<never> {
  if (res.status === 401) { handleSessionExpired(); throw new ApiError(401, "Unauthenticated"); }
  let detail = `${res.status} ${res.statusText}`;
  let body: any;
  try {
    body = await res.json();
    if (typeof body?.detail === "object" && body.detail !== null) {
      if (Array.isArray(body.detail)) {
        // Pydantic HTTPValidationError
        detail = body.detail.map((e: any) => e.msg || JSON.stringify(e)).join(", ");
      } else if (body.detail.message) {
        // Custom domain error (e.g. Workflow publish)
        let errMsg = body.detail.message;
        if (Array.isArray(body.detail.errors) && body.detail.errors.length > 0) {
          errMsg += ": " + body.detail.errors.map((e: any) => e.message).join("; ");
        }
        detail = errMsg;
      } else {
        detail = JSON.stringify(body.detail);
      }
    } else {
      // v2 wraps errors as {"error": {"code", "message"}}; without this every v2 failure
      // surfaced as the bare "403 Forbidden".
      detail = body?.detail || body?.error?.message || body?.message || detail;
    }
  } catch {
    // Preserve the HTTP fallback when the response is not JSON.
  }
  throw new ApiError(res.status, String(detail), body?.detail ?? body?.error);
}

export async function core<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${CORE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) return failure(res);
  return res.status === 204 ? (undefined as T) : (res.json() as Promise<T>);
}

/** A Core route that answers with a document rather than JSON -- a rendered
 * report's HTML. Same auth and error handling as `core`. */
export async function coreText(path: string, init?: RequestInit): Promise<string> {
  const res = await fetch(`${CORE}${path}`, { ...init, headers: { ...authHeaders(), ...init?.headers } });
  if (!res.ok) return failure(res);
  return res.text();
}

export async function intel<T>(path: string, init?: RequestInit): Promise<T> {
  return core<T>(`/api/v1/scout${path}`, init);
}

export async function downloadBlob(url: string, filename: string) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new ApiError(res.status, "Download failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
